import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import Loading from "../../../../common-components/Loading";
import { getExceptionReportAPI } from "../../../../api/reports-api";
import { calcFilterPos, calcOpDropdownPos } from "../waterfall-eac/filterPopoverUtils";
import { NumericFilterContent, TextFilterContent, OpDropdownContent, fpBase } from "../waterfall-eac/tableColumnFilterShared";
import {
  toggleSortKey,
  compareBySortKeys,
  createSortThClick,
  WaterfallThLabel,
} from "../waterfall-eac/waterfallTableUtils";
import {
  hasActiveNumFilters,
  hasActiveTextFilters,
  hasAnyTableFilters,
  sumRows,
} from "../tableTotalUtils";
import {
  TABLE_FONT,
  ACCENT_COLOR,
  TH_BG,
  TH_BG_BAND,
  TH_TEXT_MUTED,
  TH_TEXT_STRONG,
  TH_HEIGHT,
  CELL_FG,
  CELL_FG_STRONG,
  EVEN_BG,
  ODD_BG,
  TABLE_BORDER,
  TABLE_BORDER_STRONG,
  ROW_HEIGHT,
  TH_SHADOW_BOTTOM,
  TOTAL_BG,
} from "../../reportTableConfig";

const FONT = TABLE_FONT;
const SC_ACCENT = ACCENT_COLOR;

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/* ── Column definitions ── */
const COLS = [
  { key: "contractId", label: "Contract ID", isText: true, w: 140, pct: "14%" },
  { key: "projectId", label: "Project ID", isText: true, w: 170, pct: "17%" },
  { key: "empId", label: "Emp ID", isText: true, w: 110, pct: "11%" },
  { key: "empName", label: "Emp Name", isText: true, w: 212, pct: "29%" },
  { key: "revenue", label: "Revenue", isText: false, w: 130, pct: "14.5%" },
  { key: "laborCost", label: "Labor Cost", isText: false, w: 136, pct: "14.5%" },
];

const TABLE_MIN_W = COLS.reduce((s, c) => s + c.w, 0);

/* ── Formatters ── */
function fmtN(v) {
  if (v === null || v === undefined) return "";
  const n = Number(v);
  const abs = Math.abs(n).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return n < 0 ? `($${abs})` : `$${abs}`;
}

/* ── Numeric filter operators ── */
const OP_LABELS = { gt: ">", gte: "≥", lt: "<", lte: "≤", eq: "=", neq: "≠" };
const OP_ENTRIES = [
  { key: "gt", label: "> Greater than" },
  { key: "gte", label: "≥ Greater than or equal" },
  { key: "lt", label: "< Less than" },
  { key: "lte", label: "≤ Less than or equal" },
  { key: "eq", label: "= Equals" },
  { key: "neq", label: "≠ Not equal" },
];

function applyNumFilter(rawVal, filter) {
  if (!filter || filter.value === "" || filter.value === undefined) return true;
  const v = rawVal === null || rawVal === undefined ? 0 : Number(rawVal);
  const fv = Number(filter.value);
  switch (filter.op || "gt") {
    case "gt":
      return v > fv;
    case "gte":
      return v >= fv;
    case "lt":
      return v < fv;
    case "lte":
      return v <= fv;
    case "eq":
      return v === fv;
    case "neq":
      return v !== fv;
    default:
      return true;
  }
}

function mapRows(apiRows) {
  return (apiRows || []).map((r) => ({
    contractId: r.contract_id,
    projectId: r.project_id,
    empId: r.empl_id,
    empName: r.emp_name || r.empl_id,
    revenue: r.revenue,
    laborCost: r.labor_cost,
  }));
}

/* ── Style tokens ── */
const TH_SH = "inset 0 -1px 0 rgba(255,255,255,.12)";
const TH_SH_G = TH_SHADOW_BOTTOM;

const thStyle = (isLast, isNumeric) => ({
  background: TH_BG,
  color: isNumeric ? TH_TEXT_MUTED : TH_TEXT_STRONG,
  padding: "0 10px",
  height: TH_HEIGHT,
  fontWeight: 700,
  fontSize: 10,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  textAlign: isNumeric ? "right" : "left",
  whiteSpace: "nowrap",
  border: "none",
  boxShadow: isLast ? TH_SH_G : TH_SH,
  position: "sticky",
  top: 0,
  zIndex: 2,
  verticalAlign: "middle",
  overflow: "visible",
});

const tdStyle = (bg, isNumeric) => ({
  padding: "7px 10px",
  background: bg,
  color: CELL_FG,
  borderBottom: `1px solid ${TABLE_BORDER}`,
  fontSize: 12,
  whiteSpace: "nowrap",
  overflow: "hidden",
  verticalAlign: "middle",
  textAlign: isNumeric ? "right" : "left",
  fontVariantNumeric: isNumeric ? "tabular-nums" : undefined,
});

const tdTotalStyle = (isNumeric) => ({
  padding: "7px 10px",
  background: TOTAL_BG,
  color: CELL_FG_STRONG,
  fontWeight: 700,
  borderBottom: `1px solid ${TABLE_BORDER_STRONG}`,
  fontSize: 12,
  whiteSpace: "nowrap",
  textAlign: isNumeric ? "right" : "left",
  fontVariantNumeric: isNumeric ? "tabular-nums" : undefined,
  verticalAlign: "middle",
});

const filterIconBtn = (active) => ({
  flexShrink: 0,
  width: 16,
  height: 16,
  background: active ? "rgba(165,180,252,0.18)" : "transparent",
  cursor: "pointer",
  padding: 0,
  border: "none",
  borderRadius: 3,
  color: active ? "#A5B4FC" : "rgba(255,255,255,0.5)",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  verticalAlign: "middle",
});

const filterPanelStyle = {
  position: "fixed",
  zIndex: 99,
  background: "#fff",
  border: "1px solid #E2E8F0",
  borderRadius: 8,
  boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
  padding: "10px 12px",
  minWidth: 190,
  fontFamily: FONT,
};
const filterWrap = {
  display: "flex",
  alignItems: "center",
  gap: 3,
  height: 26,
};
const filterTextInput = {
  flex: 1,
  height: 26,
  border: "1.5px solid #CBD5E1",
  borderRadius: 5,
  fontSize: 11,
  padding: "0 7px",
  outline: "none",
  fontFamily: FONT,
  background: "#fff",
  color: "#111827",
};
const filterNumInput = {
  flex: 1,
  height: 26,
  border: "1.5px solid #CBD5E1",
  borderRadius: 5,
  fontSize: 11,
  padding: "0 5px",
  outline: "none",
  fontFamily: FONT,
  background: "#fff",
  color: "#111827",
  textAlign: "right",
};
const opBtn = (active) => ({
  flexShrink: 0,
  width: 22,
  height: 26,
  border: `1.5px solid ${active ? "#4F46E5" : "#CBD5E1"}`,
  borderRadius: 5,
  background: active ? "#4F46E5" : "#fff",
  fontSize: 11,
  fontWeight: 700,
  color: active ? "#fff" : "#4F46E5",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 0,
  fontFamily: FONT,
});
const clearBtn = {
  flexShrink: 0,
  width: 18,
  height: 18,
  border: "none",
  borderRadius: "50%",
  background: "#E2E8F0",
  fontSize: 9,
  color: "#64748B",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 0,
};
const opDropdownStyle = {
  position: "fixed",
  zIndex: 101,
  background: "#fff",
  border: "1px solid #E2E8F0",
  borderRadius: 8,
  boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
  minWidth: 190,
  overflow: "hidden",
  fontFamily: FONT,
};

/* ── Pagination styles ── */
const paginationBar = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "10px 20px",
  borderTop: "1px solid #E2E8F0",
  background: "#FAFAFA",
  flexShrink: 0,
  gap: 12,
  flexWrap: "wrap",
};
const paginationInfo = {
  fontSize: 12,
  color: "#6B7280",
  fontFamily: FONT,
  whiteSpace: "nowrap",
};
const paginationRight = { display: "flex", alignItems: "center", gap: 8 };
const paginationControls = { display: "flex", alignItems: "center", gap: 3 };
const pageSizeLabel = {
  fontSize: 12,
  color: "#6B7280",
  fontFamily: FONT,
  whiteSpace: "nowrap",
};
const pageSizeSelect = {
  height: 28,
  border: "1px solid #E2E8F0",
  borderRadius: 6,
  fontSize: 12,
  color: "#374151",
  padding: "0 6px",
  background: "#fff",
  cursor: "pointer",
  fontFamily: FONT,
};
const pageBtn = (disabled) => ({
  minWidth: 30,
  height: 28,
  padding: "0 6px",
  border: "1px solid #E2E8F0",
  borderRadius: 6,
  background: "#fff",
  color: disabled ? "#94A3B8" : "#374151",
  fontSize: 12,
  fontWeight: 500,
  cursor: disabled ? "default" : "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  fontFamily: FONT,
  opacity: disabled ? 0.35 : 1,
});
const pageBtnActive = {
  minWidth: 30,
  height: 28,
  padding: "0 6px",
  border: `1px solid ${TH_BG_BAND}`,
  borderRadius: 6,
  background: TH_BG_BAND,
  color: "#fff",
  fontSize: 12,
  fontWeight: 600,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  fontFamily: FONT,
};
const pageDots = {
  fontSize: 12,
  color: "#6B7280",
  padding: "0 4px",
  userSelect: "none",
};

const CARD = {
  background: "#fff",
  borderRadius: 12,
  border: "1px solid #EBEBEB",
  boxShadow: "0 1px 2px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.03)",
  overflow: "visible",
  display: "flex",
  flexDirection: "column",
  marginBottom: 20,
};
const CARD_BODY = {
  overflow: "hidden",
  borderBottomLeftRadius: 12,
  borderBottomRightRadius: 12,
  width: "100%",
};
const HEAD = {
  padding: "16px 20px 12px",
  borderBottom: "1px solid #E2E8F0",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
  position: "relative",
  zIndex: 20,
  overflow: "visible",
  flexShrink: 0,
  background: "#fff",
  borderTopLeftRadius: 12,
  borderTopRightRadius: 12,
};
const CARD_META = {
  fontSize: 12,
  fontWeight: 400,
  color: "#6B7280",
  fontFamily: FONT,
  lineHeight: 1.4,
  marginTop: 2,
};
const TITLE = {
  fontSize: 14,
  fontWeight: 600,
  color: "#111827",
  letterSpacing: "-0.01em",
  fontFamily: FONT,
  margin: 0,
};

function FilterFunnelIcon() {
  return (
    <svg
      width="10"
      height="10"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
    </svg>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   ExceptionReportTab
═══════════════════════════════════════════════════════════════════ */
export default function ExceptionReportTab({ filters, onActualsLabel, onRowCount }) {
  /* ── API data ── */
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ revenue: 0, laborCost: 0 });
  const [title, setTitle] = useState(
    "Contract / Project / Employee Exceptions",
  );

  /* ── Column filters ── */
  const [textFilters, setTextFilters] = useState({});
  const [numFilters, setNumFilters] = useState({});
  const [openFilterKey, setOpenFilterKey] = useState(null);
  const [filterPanelPos, setFilterPanelPos] = useState({ top: 0, left: 0 });
  const [openOpKey, setOpenOpKey] = useState(null);
  const [opDropdownPos, setOpDropdownPos] = useState({ top: 0, left: 0 });
  const filterBtnRefs = useRef({});
  const opBtnRefs = useRef({});
  const openFilterKeyRef = useRef(null);
  const openOpKeyRef = useRef(null);

  /* ── Pagination ── */
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  /* ── Sorting ── */
  const [sortKeys, setSortKeys] = useState([]);

  useEffect(() => {
    openFilterKeyRef.current = openFilterKey;
  }, [openFilterKey]);
  useEffect(() => {
    openOpKeyRef.current = openOpKey;
  }, [openOpKey]);

  /* ── API fetch ── */
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getExceptionReportAPI(filters || {})
      .then((res) => {
        if (cancelled) return;
        const d = res?.data;
        if (d) {
          const mapped = mapRows(d.rows);
          setRows(mapped);
          setTotals({
            revenue: d.totalRevenue ?? 0,
            laborCost: d.totalLaborCost ?? 0,
          });
          const mo =
            d.actualThroughMonth > 0 ? MONTHS[d.actualThroughMonth - 1] : null;
          const actualsStr = mo ? `Actuals through ${mo} ${new Date().getFullYear()}` : "";
          setTitle(
            `Contract / Project / Employee Exceptions — FY${d.fiscalYear}`,
          );
          if (onActualsLabel) onActualsLabel(actualsStr);
          onRowCount?.(mapped.length);
        } else {
          onRowCount?.(0);
        }
      })
      .catch(() => onRowCount?.(0))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [JSON.stringify(filters)]);

  /* ── Reposition panels on scroll / resize ── */
  useEffect(() => {
    const reposition = () => {
      const fk = openFilterKeyRef.current;
      if (fk) {
        const btn = filterBtnRefs.current[fk];
        if (btn) setFilterPanelPos(calcFilterPos(btn));
      }
      const ok = openOpKeyRef.current;
      if (ok) {
        const ob = opBtnRefs.current[ok];
        if (ob) setOpDropdownPos(calcOpDropdownPos(ob));
      }
    };
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, []);

  /* ── Close panels on outside click ── */
  useEffect(() => {
    if (!openFilterKey) return;
    const h = (e) => {
      if (
        !e.target.closest("[data-filter-panel]") &&
        !e.target.closest("[data-filter-btn]") &&
        !e.target.closest("[data-op-dd]") &&
        !e.target.closest("[data-op-dropdown]")
      )
        setOpenFilterKey(null);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [openFilterKey]);

  useEffect(() => {
    if (!openOpKey) return;
    const h = (e) => {
      if (
        !e.target.closest("[data-op-dd]") &&
        !e.target.closest("[data-op-dropdown]") &&
        !e.target.closest("[data-op-btn]")
      )
        setOpenOpKey(null);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [openOpKey]);

  useEffect(() => {
    setPage(1);
  }, [textFilters, numFilters]);

  /* ── Filter helpers ── */
  const isTextActive = (key) => !!(textFilters[key] && textFilters[key].trim());
  const isNumActive = (key) => {
    const f = numFilters[key];
    return !!(f && f.value !== "" && f.value !== undefined);
  };
  const isColActive = (col) =>
    col.isText ? isTextActive(col.key) : isNumActive(col.key);

  const toggleFilter = useCallback(
    (key) => {
      if (openFilterKey === key) {
        setOpenFilterKey(null);
        return;
      }
      const btn = filterBtnRefs.current[key];
      if (btn) setFilterPanelPos(calcFilterPos(btn));
      setOpenFilterKey(key);
      setOpenOpKey(null);
    },
    [openFilterKey],
  ); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleOpDropdown = useCallback(
    (colKey) => {
      if (openOpKey === colKey) {
        setOpenOpKey(null);
        return;
      }
      const btn = opBtnRefs.current[colKey];
      if (btn) setOpDropdownPos(calcOpDropdownPos(btn));
      setOpenOpKey(colKey);
    },
    [openOpKey],
  );

  const selectOp = (colKey, opKey) => {
    setNumFilters((prev) => ({
      ...prev,
      [colKey]: { ...prev[colKey], op: opKey },
    }));
    setOpenOpKey(null);
  };

  const updateNumFilter = (colKey, field, value) =>
    setNumFilters((prev) => ({
      ...prev,
      [colKey]: { op: "gt", ...prev[colKey], [field]: value },
    }));

  const clearFilter = (key) => {
    const col = COLS.find((c) => c.key === key);
    if (col?.isText)
      setTextFilters((prev) => {
        const n = { ...prev };
        delete n[key];
        return n;
      });
    else
      setNumFilters((prev) => {
        const n = { ...prev };
        delete n[key];
        return n;
      });
  };

  const getColLabel = (key) => COLS.find((c) => c.key === key)?.label || key;

  const renderFilterContent = (key) => {
    const col = COLS.find((c) => c.key === key);
    if (col?.isText) {
      const val = textFilters[key] || "";
      return (
        <TextFilterContent
          label={getColLabel(key)}
          value={val}
          onChange={(v) => setTextFilters((p) => ({ ...p, [key]: v }))}
          onClear={() => clearFilter(key)}
        />
      );
    }
    const f = numFilters[key] || { op: "gt", value: "" };
    return (
      <NumericFilterContent
        label={getColLabel(key)}
        value={f.value ?? ""}
        op={f.op || "gt"}
        onValueChange={(v) => updateNumFilter(key, "value", v)}
        onClear={() => clearFilter(key)}
        onToggleOp={() => toggleOpDropdown(key)}
        opBtnRef={(el) => { opBtnRefs.current[key] = el; }}
      />
    );
  };

  /* ── Filtering + sorting + pagination ── */
  const filteredRows = useMemo(() => rows.filter((row) => {
    for (const col of COLS) {
      if (col.isText) {
        const v = textFilters[col.key];
        if (
          v &&
          v.trim() &&
          !String(row[col.key] ?? "")
            .toLowerCase()
            .includes(v.trim().toLowerCase())
        )
          return false;
      } else {
        if (
          isNumActive(col.key) &&
          !applyNumFilter(row[col.key], numFilters[col.key])
        )
          return false;
      }
    }
    return true;
  }), [rows, textFilters, numFilters]);

  const getSortColumnLabel = useCallback((key) => {
    const col = COLS.find((c) => c.key === key);
    return col ? col.label : key;
  }, []);

  const getSortValue = useCallback((row, key) => {
    const col = COLS.find((c) => c.key === key);
    if (col?.isText) return row[key] ?? "";
    return row[key] ?? 0;
  }, []);

  const handleSort = useCallback((colKey) => {
    setSortKeys((prev) => toggleSortKey(prev, colKey));
  }, []);

  const onThClick = useMemo(() => createSortThClick(handleSort), [handleSort]);

  const sortedRows = useMemo(() => {
    if (sortKeys.length === 0) return filteredRows;
    return [...filteredRows].sort((a, b) => compareBySortKeys(a, b, sortKeys, getSortValue));
  }, [filteredRows, sortKeys, getSortValue]);

  const hasTableFilters = useMemo(
    () => hasAnyTableFilters(hasActiveTextFilters(textFilters), hasActiveNumFilters(numFilters)),
    [textFilters, numFilters],
  );

  const displayTotals = useMemo(() => {
    if (!hasTableFilters) return totals;
    return {
      revenue: sumRows(filteredRows, (r) => r.revenue) ?? 0,
      laborCost: sumRows(filteredRows, (r) => r.laborCost) ?? 0,
    };
  }, [hasTableFilters, filteredRows, totals]);

  const totalFiltered = sortedRows.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / pageSize));
  const safePage = Math.min(page, totalPages);
  const start = (safePage - 1) * pageSize;
  const pageRows = sortedRows.slice(start, start + pageSize);
  const pageNumbers = (() => {
    if (totalPages <= 7)
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages = new Set([
      1,
      totalPages,
      safePage,
      safePage - 1,
      safePage + 1,
      safePage - 2,
      safePage + 2,
    ]);
    return [...pages]
      .filter((p) => p >= 1 && p <= totalPages)
      .sort((a, b) => a - b);
  })();

  if (loading) return <Loading />;

  return (
    <>
      <div style={CARD}>
        {/* ── Card header ── */}
        <div style={HEAD}>
          <div>
            <h2 style={TITLE}>{title}</h2>
            {sortKeys.length > 0 && (
              <div style={CARD_META}>
                <span style={{ color: SC_ACCENT, fontWeight: 500 }}>
                  Sorted by {sortKeys.map((s) => getSortColumnLabel(s.key)).join(", ")}
                </span>
              </div>
            )}
          </div>
        </div>

        <div style={CARD_BODY}>
        {/* ── Table ── */}
        <div style={{ overflowX: "auto", width: "100%" }}>
          {rows.length === 0 ? (
            <div
              style={{
                padding: "40px 24px",
                textAlign: "center",
                color: "#9CA3AF",
                fontSize: 14,
                fontFamily: FONT,
              }}
            >
              No exceptions found for the selected filters.
            </div>
          ) : (
            <table
              style={{
                borderCollapse: "collapse",
                borderSpacing: 0,
                tableLayout: "fixed",
                fontFamily: FONT,
                fontSize: 12,
                width: "100%",
                minWidth: TABLE_MIN_W,
              }}
            >
              <colgroup>
                {COLS.map((col) => (
                  <col key={col.key} style={{ width: col.pct }} />
                ))}
              </colgroup>
              <thead>
                <tr style={{ height: 34 }}>
                  {COLS.map((col, ci) => (
                    <th
                      key={col.key}
                      style={{
                        ...thStyle(ci === COLS.length - 1, !col.isText),
                        cursor: "pointer",
                      }}
                      onClick={onThClick(col.key)}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 4,
                          minWidth: 0,
                          justifyContent: col.isText
                            ? "space-between"
                            : "flex-end",
                        }}
                      >
                        <WaterfallThLabel
                          label={col.label}
                          colKey={col.key}
                          sortKeys={sortKeys}
                          getLabel={getSortColumnLabel}
                          highlight={openFilterKey === col.key}
                          align={col.isText ? "left" : "right"}
                        />
                        <button
                          ref={(el) => {
                            filterBtnRefs.current[col.key] = el;
                          }}
                          data-filter-btn
                          style={filterIconBtn(
                            isColActive(col) || openFilterKey === col.key,
                          )}
                          onClick={() => toggleFilter(col.key)}
                          title="Filter"
                        >
                          <FilterFunnelIcon />
                        </button>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {/* Total row */}
                <tr style={{ height: 36 }}>
                  {COLS.map((col, ci) => (
                    <td key={col.key} style={tdTotalStyle(!col.isText)}>
                      {ci === 0 ? (
                        <strong>Total</strong>
                      ) : col.isText ? (
                        ""
                      ) : (
                        fmtN(displayTotals[col.key])
                      )}
                    </td>
                  ))}
                </tr>
                {/* Data rows */}
                {pageRows.map((row, i) => {
                  const bg = i % 2 === 0 ? EVEN_BG : ODD_BG;
                  return (
                    <tr
                      key={`${row.contractId}-${row.empId}-${i}`}
                      style={{ height: ROW_HEIGHT }}
                    >
                      {COLS.map((col) => (
                        <td key={col.key} style={tdStyle(bg, !col.isText)}>
                          {col.key === "contractId" ? (
                            <span style={{ fontWeight: 600, color: CELL_FG_STRONG }}>
                              {row.contractId}
                            </span>
                          ) : col.isText ? (
                            row[col.key]
                          ) : (
                            fmtN(row[col.key])
                          )}
                        </td>
                      ))}
                    </tr>
                  );
                })}
                {pageRows.length === 0 && (
                  <tr>
                    <td
                      colSpan={COLS.length}
                      style={{
                        padding: "24px 20px",
                        textAlign: "center",
                        color: "#9CA3AF",
                        fontSize: 13,
                        fontFamily: FONT,
                      }}
                    >
                      No rows match the current filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* ── Pagination footer ── */}
        {rows.length > 0 && (
          <div style={paginationBar}>
            <span style={paginationInfo}>
              {totalFiltered === 0
                ? "No rows match the current filters"
                : `${start + 1}–${Math.min(start + pageSize, totalFiltered)} of ${totalFiltered} rows`}
            </span>
            <div style={paginationRight}>
              <span style={pageSizeLabel}>Rows Per Page</span>
              <select
                style={pageSizeSelect}
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
              >
                {[10, 25, 50, 100].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
              <div style={paginationControls}>
                <button
                  style={pageBtn(safePage === 1)}
                  disabled={safePage === 1}
                  onClick={() => setPage(1)}
                  title="First"
                >
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path
                      d="M9 10L6 7l3-3M5 10V4"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                <button
                  style={pageBtn(safePage === 1)}
                  disabled={safePage === 1}
                  onClick={() => setPage((p) => p - 1)}
                  title="Previous"
                >
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path
                      d="M8.5 10.5L5 7l3.5-3.5"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                {pageNumbers.map((p, i) => (
                  <React.Fragment key={p}>
                    {i > 0 && pageNumbers[i - 1] < p - 1 && (
                      <span style={pageDots}>…</span>
                    )}
                    <button
                      style={p === safePage ? pageBtnActive : pageBtn(false)}
                      onClick={() => setPage(p)}
                    >
                      {p}
                    </button>
                  </React.Fragment>
                ))}
                <button
                  style={pageBtn(safePage === totalPages)}
                  disabled={safePage === totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  title="Next"
                >
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path
                      d="M5.5 3.5L9 7l-3.5 3.5"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                <button
                  style={pageBtn(safePage === totalPages)}
                  disabled={safePage === totalPages}
                  onClick={() => setPage(totalPages)}
                  title="Last"
                >
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path
                      d="M5 10l3-3-3-3M9 10V4"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}
        </div>
      </div>

      {/* ── Filter panel portal ── */}
      {openFilterKey &&
        createPortal(
          <div
            data-filter-panel
            style={{
              ...fpBase,
              top: filterPanelPos.top,
              left: filterPanelPos.left,
            }}
          >
            {renderFilterContent(openFilterKey)}
          </div>,
          document.body,
        )}

      {/* ── Operator dropdown portal ── */}
      {openOpKey &&
        createPortal(
          <OpDropdownContent
            activeOp={(numFilters[openOpKey]?.op) || "gt"}
            onSelect={(op) => selectOp(openOpKey, op)}
            style={{ top: opDropdownPos.top, left: opDropdownPos.left }}
          />,
          document.body,
        )}
    </>
  );
}
